import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildInsight,
  dimLabel,
  estimateAudience,
  fetchPopulationBreakdown,
  type PopulationCell,
  type PopulationDim,
} from '../../lib/populationService';
import { supabase } from '../../lib/supabase';

const DIMS: PopulationDim[] = ['sector', 'age_band', 'gender', 'department', 'position', 'contract'];
const AGE_BANDS = ['18-24', '25-29', '30-34', '35-39', '40-49', '50+'];
const COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b', '#ec4899'];

function toChartRows(cells: PopulationCell[]) {
  const xs = [...new Set(cells.map((c) => c.x))];
  const colors = [...new Set(cells.map((c) => c.color))];
  return xs.map((x) => {
    const row: Record<string, string | number> = { x };
    for (const color of colors) {
      row[color] = cells.filter((c) => c.x === x && c.color === color).reduce((s, c) => s + c.n, 0);
    }
    return row;
  });
}

export function PopulationPanel() {
  const [role, setRole] = useState<'candidate' | 'employer'>('candidate');
  const [dimX, setDimX] = useState<PopulationDim>('sector');
  const [dimColor, setDimColor] = useState<PopulationDim>('age_band');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [cells, setCells] = useState<PopulationCell[]>([]);
  const [sectors, setSectors] = useState<{ code: string; name: string }[]>([]);

  const [estSector, setEstSector] = useState('');
  const [estAge, setEstAge] = useState('');
  const [estDept, setEstDept] = useState('');
  const [estResult, setEstResult] = useState<string | null>(null);
  const [estError, setEstError] = useState<string | null>(null);
  const [estBusy, setEstBusy] = useState(false);

  useEffect(() => {
    supabase.from('sectors').select('code, name_fr').order('name_fr')
      .then(({ data }) => {
        setSectors((data || []).map((s: { code: string; name_fr: string }) => ({
          code: s.code,
          name: s.name_fr,
        })));
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPopulationBreakdown({ role, dimX, dimColor })
      .then((res) => {
        if (cancelled) return;
        setTotal(res.total);
        setCells(res.cells);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [role, dimX, dimColor]);

  const chartRows = useMemo(() => toChartRows(cells), [cells]);
  const series = useMemo(() => [...new Set(cells.map((c) => c.color))], [cells]);
  const insight = useMemo(() => buildInsight(cells, total), [cells, total]);

  const filterCount = [estSector, estAge, estDept].filter(Boolean).length;

  const runEstimate = async () => {
    setEstError(null);
    setEstResult(null);
    if (filterCount === 0) {
      setEstError('Choisissez 1 ou 2 filtres.');
      return;
    }
    if (filterCount > 2) {
      setEstError('Deux filtres maximum.');
      return;
    }
    setEstBusy(true);
    try {
      const res = await estimateAudience({
        sectorCode: estSector || undefined,
        ageBand: estAge || undefined,
        department: estDept || undefined,
      });
      setEstResult(res.underMin ? 'Moins de 50 profils' : `Environ ${res.bucket} profils`);
    } catch (err) {
      setEstError(err instanceof Error ? err.message : 'Estimation impossible');
    } finally {
      setEstBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-600">
          Population
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'candidate' | 'employer')}
            className="mt-1 block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm"
          >
            <option value="candidate">Candidats</option>
            <option value="employer">Recruteurs / offres</option>
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Axe X
          <select
            value={dimX}
            onChange={(e) => setDimX(e.target.value as PopulationDim)}
            className="mt-1 block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm"
          >
            {DIMS.map((d) => <option key={d} value={d}>{dimLabel(d)}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Couleur
          <select
            value={dimColor}
            onChange={(e) => setDimColor(e.target.value as PopulationDim)}
            className="mt-1 block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm"
          >
            {DIMS.map((d) => <option key={d} value={d}>{dimLabel(d)}</option>)}
          </select>
        </label>
        <p className="text-xs text-gray-500 mb-2">{total} profils (hors seeds)</p>
      </div>

      <p className="text-sm text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">{insight}</p>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      ) : chartRows.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">Aucune donnée pour ces axes.</p>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="x" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {series.map((s, i) => (
                <Bar key={s} dataKey={s} stackId="a" fill={COLORS[i % COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800">Estimation de cible (futur pub)</h3>
        <p className="text-xs text-gray-500 mt-1 mb-3">
          Au plus deux filtres. Pas de genre, pas de ville, pas de poste. Le compte est arrondi à la cinquantaine.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-600">
            Secteur
            <select
              value={estSector}
              onChange={(e) => setEstSector(e.target.value)}
              className="mt-1 block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm min-w-[160px]"
            >
              <option value="">—</option>
              {sectors.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Âge
            <select
              value={estAge}
              onChange={(e) => setEstAge(e.target.value)}
              className="mt-1 block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">—</option>
              {AGE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Département
            <input
              value={estDept}
              onChange={(e) => setEstDept(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="75"
              className="mt-1 block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm w-24"
            />
          </label>
          <button
            type="button"
            onClick={runEstimate}
            disabled={estBusy}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg disabled:opacity-40"
          >
            Estimer
          </button>
        </div>
        {estError ? <p className="text-xs text-red-600 mt-2">{estError}</p> : null}
        {estResult ? <p className="text-sm font-semibold text-gray-800 mt-2">{estResult}</p> : null}
      </div>
    </div>
  );
}
